import React, { useEffect, useState } from "react";
import { FilterItem } from "./FilterItem";
import { LabeledCheckbox } from "./LabeledCheckbox";
import { SidebarSortingFilters } from "./types/sortingFilters";
import { defaultFilter } from "./constants/defaultFilter";
import clsx from "clsx";
import { usePoolsStore } from "store/poolsStore";
import { SortingOrder } from "types/pagination";
import { formatInputValue } from "utils/formatInputValue";
import Button from "components/ButtonCustom";

interface SortingFiltersProps {
  isModal?: boolean;
  onChange: (filters: SidebarSortingFilters) => void;
  filters?: SidebarSortingFilters;
  disabled?: boolean;
  searchValue?: string;
  setSearchValue?: (searchValue: string) => void;
  setIsFiltersOpen?: (isFiltersOpen: boolean) => void;
}

export const SortingFilter: React.FC<SortingFiltersProps> = ({
  onChange,
  filters = defaultFilter,
  disabled,
  isModal,
  searchValue = "",
  setSearchValue = () => {},
  setIsFiltersOpen = () => {},
}) => {
  const [capitalization, setCapitalization] = useState<[number, number]>([
    0, 0,
  ]);
  const [investment, setInvestment] = useState<[number, number]>([0, 0]);
  const [volume, setVolume] = useState<[number, number]>([0, 0]);
  const [tvlOrder, setTvlOrder] = useState<SortingOrder | null>(null);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const { maxFiltersValues } = usePoolsStore();

  useEffect(() => {
    setCapitalization(filters.capitalization);

    setInvestment(filters.investment);

    setVolume(filters.volume);

    setTvlOrder(filters.tvlOrder);

    setOnlyFavorites(filters.onlyFavorites);
  }, [filters]);

  const handleSliderChange = (
    newValue: [number, number],
    callback: (value: [number, number]) => void
  ) => {
    callback(newValue);
  };

  const handleMinChange = (
    newMinValue: string,
    prevMaxValue: number,
    callback: (value: [number, number]) => void
  ) => {
    const clearedMinValue = +formatInputValue(newMinValue);

    if (!isNaN(clearedMinValue)) {
      callback([clearedMinValue, prevMaxValue]);
    }
  };

  const handleMaxChange = (
    newMaxValue: string,
    prevMinValue: number,
    callback: (value: [number, number]) => void
  ) => {
    const clearedMaxValue = +formatInputValue(newMaxValue);

    if (!isNaN(clearedMaxValue)) {
      callback([prevMinValue, clearedMaxValue]);
    }
  };

  const applyFilters = () => {
    setIsFiltersOpen(false);
    onChange({
      capitalization,
      investment,
      volume,
      tvlOrder,
      onlyFavorites,
    });
  };

  const resetFilters = () => {
    setIsFiltersOpen(false);
    onChange(defaultFilter);
  };

  const handleSetTVLOrder = (order: SortingOrder) => {
    setTvlOrder((state) => (state === order ? null : order));
  };

  return (
    <div
      className={`text-black relative w-full rounded-24 bg-white p-6 shadow-sm dark:bg-blue-darkMode-40 dark:text-white`}
    >
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-[20px] font-semibold lg:text-2xl">
          Sorting Filters
        </h2>
      </div>

      <div
        className={clsx(
          disabled ? "pointer-events-none opacity-40" : " flex gap-6 items-end"
        )}
      >
        <div className={`mb-6 ${isModal ? "mt-[12px]" : ""}`}>
          <FilterItem
            title="Capitalization"
            currentMinValue={capitalization[0]}
            currentMaxValue={capitalization[1]}
            minValue={maxFiltersValues.capitalization[0]}
            maxValue={maxFiltersValues.capitalization[1]}
            onSliderChange={(_e, newValue) =>
              handleSliderChange(
                newValue as [number, number],
                setCapitalization
              )
            }
            onMinChange={(e) =>
              handleMinChange(
                e.target.value,
                capitalization[1],
                setCapitalization
              )
            }
            onMaxChange={(e) =>
              handleMaxChange(
                e.target.value,
                capitalization[0],
                setCapitalization
              )
            }
          />
        </div>

        <div className="mb-6">
          <label className="mb-2 block font-medium">TVL</label>

          <div>
            <LabeledCheckbox
              checked={tvlOrder === SortingOrder.DESC}
              onChange={() => handleSetTVLOrder(SortingOrder.DESC)}
              id="highest"
              label="Highest to lowest"
            />
            <LabeledCheckbox
              checked={tvlOrder === SortingOrder.ASC}
              onChange={() => handleSetTVLOrder(SortingOrder.ASC)}
              id="lowest"
              label="Lowest to highest"
            />
          </div>
        </div>

        <div className="mb-6">
          <FilterItem
            title="Volume (24h)"
            currentMinValue={volume[0]}
            currentMaxValue={volume[1]}
            minValue={maxFiltersValues.volume[0]}
            maxValue={maxFiltersValues.volume[1]}
            onSliderChange={(_e, newValue) =>
              handleSliderChange(newValue as [number, number], setVolume)
            }
            onMinChange={(e) =>
              handleMinChange(e.target.value, volume[1], setVolume)
            }
            onMaxChange={(e) =>
              handleMaxChange(e.target.value, volume[0], setVolume)
            }
          />
        </div>

        <div className="flex flex-col justify-end gap-1">
          <Button onClick={applyFilters} variant="filled">
            Apply filters
          </Button>

          <Button onClick={resetFilters} variant="bordered">
            Reset filters
          </Button>
        </div>
      </div>
    </div>
  );
};
